import { animate, style, transition, trigger } from '@angular/animations';
import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TvSlide } from '@tanzspass/shared';
import { TsHttpService } from '../../services/ts-http.service';
import { TvScreenService } from '../../services/tv-screen.service';
import { TvSlideService } from '../../services/tv-slide.service';
import { PictureComponent } from '../../shared/picture/picture.component';

@Component({
  selector: 'ts-infosystem-gallery',
  templateUrl: './infosystem-gallery.component.html',
  styleUrls: ['./infosystem-gallery.component.css'],
  animations: [
    trigger('crossFade', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('1s ease-in-out', style({ opacity: 1 })),
      ]),
    ]),
  ],
  imports: [NgFor, NgIf, PictureComponent],
})
export class InfosystemGalleryComponent implements OnDestroy {
  @Input() schoolId: number;
  slides: TvSlide[] = [];
  count = 0;
  currentIndex = -1;
  animationState = 0; // For triggering cross-fade animations
  private slideInterval: ReturnType<typeof setInterval>;

  constructor(private tvScreenSrv: TvScreenService) {
    this.tvScreenSrv.payload$.pipe(takeUntilDestroyed()).subscribe((payload) => {
      this.applySlides(payload?.tvSlides ?? []);
    });
  }

  private applySlides(slides: TvSlide[]) {
    const oldCount = this.count;
    const oldSlides = [...this.slides]; // copy to compare

    this.slides = slides;
    this.count = slides.length;

    // Only (re)start the slideshow when the slide set actually changes, so a
    // routine refresh doesn't reset the current position.
    const slidesActuallyChanged =
      oldCount !== this.count ||
      JSON.stringify(oldSlides.map((s) => s.id)) !== JSON.stringify(slides.map((s) => s.id));

    if (oldCount === 0 && this.count > 0) {
      this.startSlideshow();
    } else if (slidesActuallyChanged) {
      if (this.currentIndex >= this.count) {
        this.currentIndex = this.count > 0 ? 0 : -1;
      }
      if (this.count > 0) {
        this.startSlideshow();
      }
    }
  }

  getVideoPath(filename: string) {
    return `${TsHttpService.assetsRoot}${filename}`;
  }

  incIndex() {
    if (this.count > 0) {
      this.currentIndex = this.currentIndex < this.count - 1 ? this.currentIndex + 1 : 0;
      this.animationState++; // Trigger animation
    }
  }

  startSlideshow() {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }

    if (this.count > 0) {
      this.currentIndex = 0; // Start with first slide
      this.slideInterval = setInterval(() => {
        this.incIndex();
      }, TvSlideService.slideDuration);
    }
  }

  ngOnDestroy(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }
}
